import type { CartesianPosition } from '@sb/geometry';
import { cartesianDistance } from '@sb/geometry';

/**
 * Sphere-radius trim window for a demonstration replay. `side='last'` picks
 * the final contiguous in-sphere run (trims lead-in / approach to the
 * reference); `side='first'` picks the first contiguous in-sphere run (trims
 * lead-out / departure from the reference) — used by pin=start.
 *
 * Returns inclusive `[k, m]`. `null` when no frame lies inside the sphere; the
 * caller treats that as operator error and reports `closestApproachMeters`.
 */
export function computeSphereWindow(
  positions: CartesianPosition[],
  center: CartesianPosition,
  radiusMeters: number,
  side: 'first' | 'last' = 'last',
): { k: number; m: number } | null {
  const lastIndex = positions.length - 1;
  if (lastIndex < 0) return null;

  const inside = positions.map(
    (p) => cartesianDistance(p, center) <= radiusMeters,
  );

  if (side === 'first') {
    let k = -1;

    for (let i = 0; i <= lastIndex; i += 1) {
      if (inside[i]) {
        k = i;
        break;
      }
    }

    if (k < 0) return null;

    let m = k;
    while (m < lastIndex && inside[m + 1]) m += 1;

    return { k, m };
  }

  let m = -1;

  for (let i = lastIndex; i >= 0; i -= 1) {
    if (inside[i]) {
      m = i;
      break;
    }
  }

  if (m < 0) return null;

  let k = m;
  while (k > 0 && inside[k - 1]) k -= 1;

  return { k, m };
}

/**
 * Smallest distance from any recorded position to `center`. Used to build a
 * helpful failure message when {@link computeSphereWindow} returns `null`
 * (so the operator knows the minimum radius that would select something).
 * Returns `Infinity` for an empty array.
 */
export function closestApproachMeters(
  positions: CartesianPosition[],
  center: CartesianPosition,
): number {
  let min = Infinity;

  for (const p of positions) {
    const d = cartesianDistance(p, center);
    if (d < min) min = d;
  }

  return min;
}
